import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import api from '../../services/api';

/**
 * @typedef {Object} ChecklistTemplateDialogProps
 * @property {boolean} open - Whether the dialog is open.
 * @property {Function} onClose - Function to close the dialog.
 * @property {Function} onSelect - Function called with the selected template (with items).
 */

/**
 * Dialog for selecting a checklist template.
 * @component
 * @param {ChecklistTemplateDialogProps} props
 * @returns {JSX.Element} Checklist template selection dialog.
 */
const ChecklistTemplateDialog = ({ open, onClose, onSelect }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get('checklist-template/')
      .then((response) => setTemplates(response.data.payload))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open]);

  /**
   * Expands/collapses a template and loads its items if needed.
   * @param {number} templateId
   */
  const handleExpand = async (templateId) => {
    const expanded = !expandedItems[templateId];
    setExpandedItems((prev) => ({ ...prev, [templateId]: expanded }));
    if (expanded) {
      try {
        const response = await api.get(`checklist-template-items/${templateId}`);
        setTemplates((prev) =>
          prev.map((t) =>
            t.id === templateId ? { ...t, items: response.data.payload } : t
          )
        );
      } catch {
        // ignore
      }
    }
  };

  /**
   * Selects a template and loads its items if not already loaded.
   * @param {Object} template
   */
  const handleTemplateSelect = async (template) => {
    setSelectedTemplateId(template.id);
    if (template.items && Array.isArray(template.items)) return;
    try {
      const response = await api.get(`checklist-template-items/${template.id}`);
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === template.id ? { ...t, items: response.data.payload } : t
        )
      );
    } catch {
      // ignore
    }
  };

  /**
   * Gets the selected template with its items loaded.
   * @returns {Object|null}
   */
  const getSelectedTemplateWithItems = () => {
    const tpl = templates.find((t) => t.id === selectedTemplateId);
    return tpl && tpl.items ? tpl : null;
  };

  /**
   * Handles the select button click.
   */
  const handleSelect = () => {
    const tpl = getSelectedTemplateWithItems();
    if (tpl) {
      onSelect(tpl);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography align="center" variant="subtitle1">
          Select Checklist Template
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && <Typography>Loading...</Typography>}
        {!loading && (
          <List>
            {templates.map((template) => (
              <React.Fragment key={template.id}>
                <ListItem
                  disableGutters
                  selected={selectedTemplateId === template.id}
                  onClick={() => handleTemplateSelect(template)}
                  sx={{ cursor: 'pointer' }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpand(template.id);
                      }}
                    >
                      {expandedItems[template.id] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  }
                >
                  <Checkbox
                    checked={selectedTemplateId === template.id}
                    onChange={() => handleTemplateSelect(template)}
                    tabIndex={-1}
                    inputProps={{ 'aria-label': 'Select template' }}
                  />
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                        {template.template_name}
                      </Typography>
                    }
                  />
                </ListItem>
                {expandedItems[template.id] && (
                  <List component="div" disablePadding sx={{ pl: 10 }}>
                    {template.items &&
                      template.items.map((item) => (
                        <ListItem key={item.id} disableGutters>
                          <ListItemText
                            primary={
                              <Typography variant="body2">{item.description}</Typography>
                            }
                          />
                        </ListItem>
                      ))}
                  </List>
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSelect}
          color="primary"
          disabled={!getSelectedTemplateWithItems()}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChecklistTemplateDialog;
